/**
 * The dedicated sign-in page.
 *
 * The modal is still the right thing for someone already reading the directory:
 * it opens over the page they were on and closing it puts them back. It is the
 * wrong thing for someone who arrives cold on a login URL from an email, a
 * bookmark or a shared link, because a dialog floating over a directory they
 * did not ask for reads as an interruption rather than a destination.
 *
 * So both exist, deliberately. The modal handles in-app auth; this page handles
 * arriving-to-sign-in. They share their copy through lib/authCopy so the two
 * cannot drift apart, and they call the same two tRPC mutations.
 *
 * This file previously held the Manus-era login page, which was orphaned in Aug
 * 2026 when the modal replaced it: it was left on disk but never routed. It has
 * been rewritten rather than restored.
 */

import { useEffect, useState } from "react";
import { BUTTON } from "@/lib/buttonStyles";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { trpc } from "@/lib/trpc";
import { Link, useSearch } from "wouter";
import { motion } from "framer-motion";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { useAuth } from "@/core/hooks/useAuth";
import { readRedirectParam } from "@/lib/safeRedirect";
import { AUTH_BULLETS, AUTH_COPY, AUTH_PRIVACY_LINE } from "@/lib/authCopy";

import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
const HERO_BG = "/assets/hero-bg-Y8F6k5ayA9btVQcqwjUfpV.webp";

type Mode = "login" | "signup";

export default function Login() {
  const searchString = useSearch();
  const { user, loading } = useAuth();

  /* Read once on mount. The destination is a property of the link that was
     clicked, and re-reading it as the query string changes would let a later
     navigation move the target out from under a half-filled form. */
  const [destination] = useState(() => readRedirectParam(searchString));

  /* Login first: this page exists for people who already have an account and
     are trying to get back in. `?mode=signup` is honoured so a campaign can
     point at the same page for new visitors. */
  const [mode, setMode] = useState<Mode>(() =>
    new URLSearchParams(searchString).get("mode") === "signup"
      ? "signup"
      : "login"
  );

  const [firstName, setFirstName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const utils = trpc.useUtils();
  const signupMutation = trpc.auth.signup.useMutation();
  const loginMutation = trpc.auth.login.useMutation();

  /* Someone who is already signed in and lands here from an old bookmark or a
     welcome email they opened twice should not be asked to sign in again. Sent
     on to wherever the link was pointing instead. */
  useEffect(() => {
    if (!loading && user) window.location.href = destination;
  }, [loading, user, destination]);

  const finish = async () => {
    await utils.auth.me.invalidate();
    /* A full navigation rather than a client-side one, so the session cookie
       set by the mutation is present on the request for the next page. The
       destination has already been through readRedirectParam. */
    window.location.href = destination;
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !password.trim()) {
      toast.error("Please fill in all fields");
      return;
    }
    setIsLoading(true);
    try {
      await loginMutation.mutateAsync({ email, password });
      toast.success("Welcome back!");
      await finish();
    } catch (error: any) {
      toast.error(error?.message || "Login failed. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!firstName.trim() || !email.trim() || !password.trim()) {
      toast.error("Please fill in all fields");
      return;
    }
    setIsLoading(true);
    try {
      await signupMutation.mutateAsync({ firstName, email, password });
      toast.success("Welcome!");
      await finish();
    } catch (error: any) {
      toast.error(error?.message || "Signup failed. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const copy = AUTH_COPY[mode];

  /* Carry the destination onto the password reset trip, so someone who has to
     reset on the way through still lands where the original link was aimed. */
  const forgotHref =
    destination === "/"
      ? "/auth/forgot-password"
      : `/auth/forgot-password?returnTo=${encodeURIComponent(destination)}`;

  return (
    /* flex column with a flex-1 section, so the footer is pinned to the
       bottom of the viewport rather than floating halfway up a short page. */
    <div className="min-h-screen bg-background flex flex-col">
      <SiteHeader showPrimaryAction={false} />

      <section className="flex-1 relative overflow-hidden min-h-[calc(100vh-3.5rem)] flex items-center">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${HERO_BG})` }}
        />
        <div className="absolute inset-0 bg-background/55" />

        <div className="relative container py-12 sm:py-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="mx-auto max-w-4xl"
          >
            <div className="text-center mb-7">
              <p className="text-xs font-bold tracking-widest uppercase text-muted-foreground mb-2">
                Your Smart Stack
              </p>
              <h1 className="font-['Fraunces'] text-3xl sm:text-4xl font-bold text-foreground leading-tight">
                {copy.title}
              </h1>
              <p className="text-muted-foreground mt-2 max-w-xl mx-auto">
                {copy.description}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-[1fr_360px] gap-6 md:gap-8 items-start">
              {/* LEFT: what the account is for.
                  The modal shows a video here. A page reached from an email is
                  not the place to start a two minute walkthrough: the person
                  clicked a link that said sign in, so this says what is behind
                  the form and gets out of the way. */}
              <div className="order-2 md:order-1">
                <h2 className="font-['Fraunces'] text-lg font-semibold text-foreground mb-3">
                  What is waiting inside
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
                  {AUTH_BULLETS.map(bullet => (
                    <div
                      key={bullet}
                      className="flex items-center gap-2.5 bg-white border border-[#e8e0d4] rounded-lg px-3 py-2 shadow-sm"
                    >
                      <CheckCircle2 className="w-4 h-4 text-[oklch(0.38_0.09_155)] shrink-0" />
                      <span
                        className="font-medium text-foreground/80 leading-tight"
                        style={{ fontSize: 16 }}
                      >
                        {bullet}
                      </span>
                    </div>
                  ))}
                </div>
                <p className="text-sm text-muted-foreground mt-4">
                  Not sure yet?{" "}
                  <Link
                    href="/"
                    className="text-primary hover:underline font-medium"
                  >
                    Browse the directory first
                  </Link>
                  . It is free and open, no account needed.
                </p>
              </div>

              {/* RIGHT: the form.
                  Same two mutations as the dialog, same field labels. The panel
                  holds a minimum height so switching modes does not resize the
                  card under the pointer. */}
              <div className="order-1 md:order-2 bg-white rounded-xl border border-[#e8e0d4] shadow-[0_4px_24px_rgba(0,0,0,0.07)] p-5 flex flex-col md:min-h-[430px]">
                <div className="flex rounded-lg border border-[#e8e0d4] overflow-hidden mb-5">
                  <button
                    type="button"
                    onClick={() => setMode("login")}
                    className={`flex-1 inline-flex items-center justify-center min-h-11 text-sm font-semibold transition-colors ${
                      mode === "login"
                        ? "bg-primary text-primary-foreground"
                        : "bg-[#f5f0e8] text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    Sign In
                  </button>
                  <button
                    type="button"
                    onClick={() => setMode("signup")}
                    className={`flex-1 inline-flex items-center justify-center min-h-11 text-sm font-semibold transition-colors ${
                      mode === "signup"
                        ? "bg-primary text-primary-foreground"
                        : "bg-[#f5f0e8] text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    Create Account
                  </button>
                </div>

                <div className="flex-1 flex flex-col justify-center">
                  <h2 className="font-['Fraunces'] text-lg font-semibold text-foreground mb-0.5">
                    {copy.formHeading}
                  </h2>
                  <p className="text-xs text-muted-foreground mb-4">
                    {copy.formSubheading}
                  </p>

                  {mode === "login" ? (
                    <form onSubmit={handleLogin} className="space-y-3">
                      <div>
                        <label
                          htmlFor="login-email"
                          className="block text-xs font-medium text-foreground mb-1"
                        >
                          Email
                        </label>
                        <Input
                          id="login-email"
                          type="email"
                          autoComplete="email"
                          placeholder="your@email.com"
                          value={email}
                          onChange={e => setEmail(e.target.value)}
                          disabled={isLoading}
                          className="w-full bg-white h-9 text-sm"
                        />
                      </div>
                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <label
                            htmlFor="login-password"
                            className="block text-xs font-medium text-foreground"
                          >
                            Password
                          </label>
                          <Link
                            href={forgotHref}
                            className="text-xs text-primary hover:underline font-medium"
                          >
                            Forgot password?
                          </Link>
                        </div>
                        <Input
                          id="login-password"
                          type="password"
                          autoComplete="current-password"
                          placeholder="Your password"
                          value={password}
                          onChange={e => setPassword(e.target.value)}
                          disabled={isLoading}
                          className="w-full bg-white h-9 text-sm"
                        />
                      </div>
                      <button
                        type="submit"
                        disabled={isLoading}
                        className={`w-full ${BUTTON.primary}`}
                      >
                        {isLoading ? (
                          "Signing in..."
                        ) : (
                          <>
                            Sign In <ArrowRight className="w-4 h-4" />
                          </>
                        )}
                      </button>
                      <p className="text-xs text-muted-foreground text-center pt-1">
                        No account yet?{" "}
                        <button
                          type="button"
                          onClick={() => setMode("signup")}
                          className="text-primary hover:underline font-medium"
                        >
                          Create one free
                        </button>
                      </p>
                    </form>
                  ) : (
                    <form onSubmit={handleSignup} className="space-y-3">
                      <div>
                        <label
                          htmlFor="signup-firstName"
                          className="block text-xs font-medium text-foreground mb-1"
                        >
                          First Name
                        </label>
                        <Input
                          id="signup-firstName"
                          type="text"
                          autoComplete="given-name"
                          placeholder="Your first name"
                          value={firstName}
                          onChange={e => setFirstName(e.target.value)}
                          disabled={isLoading}
                          className="w-full bg-white h-9 text-sm"
                        />
                      </div>
                      <div>
                        <label
                          htmlFor="signup-email"
                          className="block text-xs font-medium text-foreground mb-1"
                        >
                          Email
                        </label>
                        <Input
                          id="signup-email"
                          type="email"
                          autoComplete="email"
                          placeholder="your@email.com"
                          value={email}
                          onChange={e => setEmail(e.target.value)}
                          disabled={isLoading}
                          className="w-full bg-white h-9 text-sm"
                        />
                      </div>
                      <div>
                        <label
                          htmlFor="signup-password"
                          className="block text-xs font-medium text-foreground mb-1"
                        >
                          Password
                        </label>
                        <Input
                          id="signup-password"
                          type="password"
                          autoComplete="new-password"
                          placeholder="At least 6 characters"
                          value={password}
                          onChange={e => setPassword(e.target.value)}
                          disabled={isLoading}
                          className="w-full bg-white h-9 text-sm"
                        />
                      </div>
                      <button
                        type="submit"
                        disabled={isLoading}
                        className={`w-full ${BUTTON.primary}`}
                      >
                        {isLoading ? (
                          "Creating account..."
                        ) : (
                          <>
                            Get Instant Access{" "}
                            <ArrowRight className="w-4 h-4" />
                          </>
                        )}
                      </button>
                      <p className="text-xs text-muted-foreground text-center pt-1">
                        Already have an account?{" "}
                        <button
                          type="button"
                          onClick={() => setMode("login")}
                          className="text-primary hover:underline font-medium"
                        >
                          Sign in
                        </button>
                      </p>
                    </form>
                  )}
                </div>

                <p className="text-xs text-muted-foreground text-center mt-4">
                  {AUTH_PRIVACY_LINE}
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
